import React from 'react';
import { Card } from '../components/ui/card';
import { ShieldCheck, Database, Mail, Trash2 } from 'lucide-react';

/**
 * Politique de confidentialité — RGPD conforme (art. 12-14 RGPD).
 * NOTE Jeremy: renseigner [RAISON SOCIALE], [DPO EMAIL], [ADRESSE].
 */
export default function LegalRGPD() {
  return (
    <div className="max-w-4xl mx-auto py-8 px-4 space-y-6" data-testid="legal-rgpd-page">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10">
          <ShieldCheck className="w-7 h-7 text-primary" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold">Politique de confidentialité</h1>
        <p className="text-sm text-muted-foreground">Protection de vos données personnelles — Conforme RGPD</p>
        <p className="text-xs text-muted-foreground">Dernière mise à jour : février 2026</p>
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <Card className="p-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Aucune revente</div>
            <div className="text-xs text-muted-foreground">Vos données ne sont jamais vendues à des tiers</div>
          </div>
        </Card>
        <Card className="p-4 flex items-start gap-3">
          <Database className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Chiffrement</div>
            <div className="text-xs text-muted-foreground">TLS 1.3 en transit · mots de passe bcrypt</div>
          </div>
        </Card>
        <Card className="p-4 flex items-start gap-3">
          <Trash2 className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-sm font-semibold">Suppression</div>
            <div className="text-xs text-muted-foreground">Effacement définitif sur demande sous 30 jours</div>
          </div>
        </Card>
      </div>

      <Card className="p-6 text-sm leading-relaxed space-y-4">
        <section>
          <h2 className="text-lg font-bold mt-0">1. Responsable du traitement</h2>
          <p>Le responsable du traitement des données personnelles est [RAISON SOCIALE], [FORME JURIDIQUE], immatriculée sous le numéro SIREN [SIREN], dont le siège social est situé [ADRESSE COMPLÈTE].</p>
          <p><strong>Contact RGPD :</strong> <a href="mailto:contact@tradex-ai.fr" className="text-primary hover:underline">contact@tradex-ai.fr</a></p>
        </section>

        <section>
          <h2 className="text-lg font-bold">2. Données collectées</h2>
          <p>Nous collectons uniquement les données nécessaires au fonctionnement du Service :</p>
          <table className="w-full text-xs mt-2 border-collapse">
            <thead className="border-b border-white/10">
              <tr>
                <th className="text-left p-2">Donnée</th>
                <th className="text-left p-2">Finalité</th>
                <th className="text-left p-2">Base légale</th>
                <th className="text-left p-2">Durée</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/5"><td className="p-2">Email, nom</td><td className="p-2">Création du compte, authentification</td><td className="p-2">Exécution du contrat</td><td className="p-2">Compte + 3 ans</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Mot de passe (hashé bcrypt)</td><td className="p-2">Authentification sécurisée</td><td className="p-2">Exécution du contrat</td><td className="p-2">Compte + 3 ans</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Graphiques uploadés</td><td className="p-2">Analyse par IA</td><td className="p-2">Exécution du contrat</td><td className="p-2">7 jours (cache)</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Trades journalisés</td><td className="p-2">Historique personnel, statistiques</td><td className="p-2">Exécution du contrat</td><td className="p-2">Vie du compte</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Adresse IP, User-Agent</td><td className="p-2">Sécurité, prévention fraude</td><td className="p-2">Intérêt légitime</td><td className="p-2">12 mois</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Données de paiement</td><td className="p-2">Facturation via Stripe</td><td className="p-2">Obligation légale (comptabilité)</td><td className="p-2">10 ans</td></tr>
              <tr className="border-b border-white/5"><td className="p-2">Emails ouverts / cliqués</td><td className="p-2">Optimisation des campagnes</td><td className="p-2">Intérêt légitime</td><td className="p-2">24 mois</td></tr>
              <tr><td className="p-2">Cookies techniques</td><td className="p-2">Session, préférences</td><td className="p-2">Nécessaire au service</td><td className="p-2">13 mois max</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2 className="text-lg font-bold">3. Sous-traitants</h2>
          <p>Certaines données sont transmises à des sous-traitants pour l&apos;exécution du Service :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>OpenAI (États-Unis)</strong> — Analyse par IA des graphiques. Encadré par les Clauses Contractuelles Types (CCT) et un accord API business avec « zero data retention ». Les graphiques ne sont pas utilisés pour entraîner les modèles.</li>
            <li><strong>Stripe (Irlande)</strong> — Traitement des paiements. Certifié PCI DSS niveau 1. Nous ne stockons jamais de données de carte bancaire.</li>
            <li><strong>Resend (États-Unis)</strong> — Envoi des emails transactionnels et marketing. Encadré par CCT.</li>
            <li><strong>Emergent Cloud (Union européenne)</strong> — Hébergeur cloud principal. Données stockées dans l&apos;UE.</li>
            <li><strong>MongoDB Atlas (UE)</strong> — Base de données NoSQL, région européenne.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold">4. Cookies et traceurs</h2>
          <p>Nous utilisons uniquement des cookies strictement nécessaires au fonctionnement du Service (session d&apos;authentification, préférences de langue et de thème). Aucun cookie publicitaire ou tiers n&apos;est déposé sans votre consentement explicite.</p>
          <p>Le cookie <code>tradex_affiliate</code> (localStorage, 30 jours) enregistre uniquement un code de parrainage — aucune donnée personnelle.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">5. Vos droits</h2>
          <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Droit d&apos;accès</strong> : obtenir une copie de vos données</li>
            <li><strong>Droit de rectification</strong> : corriger vos informations depuis <a href="/account/settings" className="text-primary hover:underline">/account/settings</a></li>
            <li><strong>Droit à l&apos;effacement</strong> : supprimer votre compte et toutes vos données. Effacement définitif sous 30 jours (hors obligations comptables légales).</li>
            <li><strong>Droit à la portabilité</strong> : exporter vos trades et analyses en format standard (CSV)</li>
            <li><strong>Droit à la limitation</strong> : suspendre temporairement le traitement</li>
            <li><strong>Droit d&apos;opposition</strong> : vous opposer au traitement pour motif légitime</li>
            <li><strong>Droit de retirer votre consentement</strong> aux emails marketing à tout moment (lien « se désinscrire » en pied de chaque email)</li>
          </ul>
          <p className="mt-2">Pour exercer vos droits, contactez-nous à <a href="mailto:contact@tradex-ai.fr" className="text-primary hover:underline">contact@tradex-ai.fr</a>. Nous répondons sous 30 jours.</p>
        </section>

        <section>
          <h2 className="text-lg font-bold">6. Sécurité</h2>
          <p>Nous mettons en œuvre les mesures techniques et organisationnelles suivantes :</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Chiffrement TLS 1.3 pour toutes les communications</li>
            <li>Mots de passe hashés (bcrypt) — jamais stockés en clair</li>
            <li>Tokens JWT signés (algorithme HS256)</li>
            <li>Accès admin restreint et audité</li>
            <li>Sauvegardes chiffrées quotidiennes</li>
            <li>Monitoring des accès suspects et anti-bruteforce</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-bold">7. Réclamations</h2>
          <p>En cas de désaccord persistant, vous pouvez introduire une réclamation auprès de la CNIL :</p>
          <address className="not-italic mt-2 text-muted-foreground">
            Commission Nationale de l&apos;Informatique et des Libertés (CNIL)<br />
            3 Place de Fontenoy - TSA 80715<br />
            75334 PARIS CEDEX 07<br />
            <a href="https://www.cnil.fr/fr/plaintes" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">cnil.fr/fr/plaintes</a>
          </address>
        </section>

        <section className="mt-6 pt-4 border-t border-white/10 text-xs text-muted-foreground flex items-center gap-2">
          <Mail className="w-4 h-4" /> Une question sur vos données ? <a href="/contact" className="text-primary hover:underline">Contactez-nous</a>.
        </section>
      </Card>
    </div>
  );
}
